/**
 * ErrorHandler
 *
 * 統一されたエラーハンドリングとユーザーフィードバック管理
 * 各エラーケースに対して適切な日本語メッセージを提供
 */

// エラータイプの定義
export const ErrorType = {
  CONFIG_NOT_FOUND: 'CONFIG_NOT_FOUND',
  CONFIG_PARSE_ERROR: 'CONFIG_PARSE_ERROR',
  CONFIG_VALIDATION_ERROR: 'CONFIG_VALIDATION_ERROR',
  MIND_FILE_NOT_FOUND: 'MIND_FILE_NOT_FOUND',
  MIND_FILE_LOAD_ERROR: 'MIND_FILE_LOAD_ERROR',
  VIDEO_LOAD_ERROR: 'VIDEO_LOAD_ERROR',
  VIDEO_NOT_FOUND: 'VIDEO_NOT_FOUND',
  CAMERA_ACCESS_DENIED: 'CAMERA_ACCESS_DENIED',
  CAMERA_NOT_AVAILABLE: 'CAMERA_NOT_AVAILABLE',
  CAMERA_NOT_FOUND: 'CAMERA_NOT_FOUND',
  CAMERA_IN_USE: 'CAMERA_IN_USE',
  CAMERA_ERROR: 'CAMERA_ERROR',
  POSTER_NOT_FOUND: 'POSTER_NOT_FOUND',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TARGET_LOAD_ERROR: 'TARGET_LOAD_ERROR',
  DETECTION_FAILED: 'DETECTION_FAILED',
  DETECTION_TIMEOUT: 'DETECTION_TIMEOUT',
  QR_INVALID: 'QR_INVALID',
  INITIALIZATION_ERROR: 'INITIALIZATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

// エラーレベルの定義
export const ErrorLevel = {
  CRITICAL: 'critical', // アプリケーションが継続不可
  ERROR: 'error', // 機能が動作しない
  WARNING: 'warning', // 一部機能に影響
  INFO: 'info', // 情報提示
};

/**
 * Issue #36: MindAR は getUserMedia 拒否時に reject() を引数無しで呼ぶ経路がある
 * (node_modules/mind-ar/src/image-target/three.js)。undefined が catch に到達したら
 * カメラ拒否確定として扱う。素の NotAllowedError も同じ扱い。
 * この判定を各 catch でコピペすると MindAR 挙動が変わった時 (別プロパティで reject 等)
 * 全箇所修正が必要になるため、1 箇所に集約している。
 */
export function isCameraDenied(error) {
  return error === undefined || error?.name === 'NotAllowedError';
}

export class ErrorHandler {
  /**
   * @param {Object} options - 設定オプション
   * @param {Function} options.onError - エラーコールバック (error, level, userMessage) => void
   * @param {boolean} options.showConsoleErrors - コンソールにエラーを出力（デフォルト: true）
   */
  constructor(options = {}) {
    this.onError = options.onError || null;
    this.showConsoleErrors = options.showConsoleErrors !== false;
    this.errorHistory = [];
    this.maxHistorySize = 50;
  }

  /**
   * エラーを処理
   * @param {Error} error - エラーオブジェクト
   * @param {string} errorType - ErrorType からのエラータイプ
   * @param {Object} context - 追加コンテキスト情報
   */
  handleError(error, errorType = ErrorType.UNKNOWN_ERROR, context = {}) {
    const errorInfo = this._createErrorInfo(error, errorType, context);

    // エラー履歴に追加
    this._addToHistory(errorInfo);

    // コンソールに出力
    if (this.showConsoleErrors) {
      this._logToConsole(errorInfo);
    }

    // コールバックを呼び出し
    if (this.onError) {
      this.onError(error, errorInfo.level, errorInfo.userMessage, errorInfo);
    }

    return errorInfo;
  }

  /**
   * エラー情報を作成
   * @param {Error} error - エラーオブジェクト
   * @param {string} errorType - エラータイプ
   * @param {Object} context - コンテキスト
   * @returns {Object} エラー情報
   * @private
   */
  _createErrorInfo(error, errorType, context) {
    const timestamp = Date.now();
    const userMessage = this._getUserMessage(errorType, context);
    const level = this._getErrorLevel(errorType);
    const troubleshooting = this._getTroubleshootingHints(errorType, context);

    // error は undefined/null/string/plain object 等が渡ってくる可能性がある。
    // MindAR は getUserMedia 拒否時に reject() を引数無しで呼ぶ経路があり、そこから
    // undefined が伝播する。ここで落ちると catch 全体がサイレントに死ぬ (Issue #36)。
    return {
      timestamp,
      errorType,
      level,
      userMessage,
      technicalMessage: error?.message ?? String(error),
      stack: error?.stack,
      context,
      troubleshooting,
      error,
    };
  }

  /**
   * ユーザー向けメッセージを取得
   * @param {string} errorType - エラータイプ
   * @param {Object} context - コンテキスト
   * @returns {string}
   * @private
   */
  _getUserMessage(errorType, context) {
    const messages = {
      [ErrorType.CONFIG_NOT_FOUND]: '設定ファイルが見つかりません',
      [ErrorType.CONFIG_PARSE_ERROR]: '設定ファイルの形式が正しくありません',
      [ErrorType.CONFIG_VALIDATION_ERROR]: '設定ファイルの内容に問題があります',
      [ErrorType.MIND_FILE_NOT_FOUND]: 'マーカー定義ファイルが見つかりません',
      [ErrorType.MIND_FILE_LOAD_ERROR]: 'マーカー定義ファイルが読み込めません',
      [ErrorType.VIDEO_LOAD_ERROR]: `動画ファイルが読み込めません${context.videoPath ? `: ${context.videoPath}` : ''}`,
      [ErrorType.VIDEO_NOT_FOUND]: `動画ファイルが見つかりません${context.videoPath ? `: ${context.videoPath}` : ''}`,
      [ErrorType.CAMERA_ACCESS_DENIED]: 'カメラへのアクセスが拒否されました',
      [ErrorType.CAMERA_NOT_AVAILABLE]: 'カメラが利用できません',
      [ErrorType.CAMERA_NOT_FOUND]: 'カメラが見つかりません',
      [ErrorType.CAMERA_IN_USE]: 'カメラが他のアプリで使用中です',
      [ErrorType.CAMERA_ERROR]: 'カメラの起動に失敗しました',
      [ErrorType.POSTER_NOT_FOUND]: '指定されたポスターが見つかりません',
      [ErrorType.NETWORK_ERROR]: 'ネットワークエラーが発生しました',
      [ErrorType.TARGET_LOAD_ERROR]: 'マーカーデータの読み込みに失敗しました',
      [ErrorType.DETECTION_FAILED]: 'マーカーの検出処理を開始できませんでした',
      [ErrorType.DETECTION_TIMEOUT]: 'マーカーが検出できません',
      [ErrorType.QR_INVALID]: 'このQRコードには対応していません',
      [ErrorType.INITIALIZATION_ERROR]: 'AR機能の初期化に失敗しました',
      [ErrorType.UNKNOWN_ERROR]: '予期しないエラーが発生しました',
    };

    return messages[errorType] || messages[ErrorType.UNKNOWN_ERROR];
  }

  /**
   * エラーレベルを取得
   * @param {string} errorType - エラータイプ
   * @returns {string}
   * @private
   */
  _getErrorLevel(errorType) {
    const criticalErrors = [
      ErrorType.CAMERA_ACCESS_DENIED,
      ErrorType.CAMERA_NOT_AVAILABLE,
      ErrorType.CAMERA_NOT_FOUND,
      ErrorType.CAMERA_IN_USE,
      ErrorType.CAMERA_ERROR,
      ErrorType.MIND_FILE_NOT_FOUND,
    ];

    const errors = [
      ErrorType.CONFIG_NOT_FOUND,
      ErrorType.CONFIG_PARSE_ERROR,
      ErrorType.MIND_FILE_LOAD_ERROR,
      ErrorType.INITIALIZATION_ERROR,
      ErrorType.TARGET_LOAD_ERROR,
      ErrorType.DETECTION_FAILED,
      ErrorType.POSTER_NOT_FOUND,
      ErrorType.NETWORK_ERROR,
    ];

    const warnings = [
      ErrorType.VIDEO_LOAD_ERROR,
      ErrorType.VIDEO_NOT_FOUND,
      ErrorType.CONFIG_VALIDATION_ERROR,
      ErrorType.QR_INVALID,
      ErrorType.DETECTION_TIMEOUT,
    ];

    if (criticalErrors.includes(errorType)) {
      return ErrorLevel.CRITICAL;
    } else if (errors.includes(errorType)) {
      return ErrorLevel.ERROR;
    } else if (warnings.includes(errorType)) {
      return ErrorLevel.WARNING;
    } else {
      return ErrorLevel.INFO;
    }
  }

  /**
   * トラブルシューティングヒントを取得
   * @param {string} errorType - エラータイプ
   * @param {Object} context - コンテキスト
   * @returns {Array<string>}
   * @private
   */
  _getTroubleshootingHints(errorType, _context) {
    const hints = {
      [ErrorType.CONFIG_NOT_FOUND]: [
        'URLパラメータで指定した設定ファイルパスを確認してください',
        'サーバーに設定ファイルが存在するか確認してください',
      ],
      [ErrorType.CONFIG_PARSE_ERROR]: [
        '設定ファイルがJSON形式で記述されているか確認してください',
        'JSONの構文エラー（カンマ、括弧の不一致）がないか確認してください',
      ],
      [ErrorType.CONFIG_VALIDATION_ERROR]: [
        '必須フィールド（posterId, mindFilePath, markers）が含まれているか確認してください',
        'マーカーIDが重複していないか確認してください',
        'boundsの値が0-1の範囲内か確認してください',
      ],
      [ErrorType.MIND_FILE_NOT_FOUND]: [
        '.mindファイルのパスが正しいか確認してください',
        '.mindファイルがサーバーに存在するか確認してください',
      ],
      [ErrorType.MIND_FILE_LOAD_ERROR]: [
        '.mindファイルが破損していないか確認してください',
        'MindAR Compilerで正しく生成されたファイルか確認してください',
      ],
      [ErrorType.VIDEO_LOAD_ERROR]: [
        '動画ファイルのパスが正しいか確認してください',
        '動画ファイルの形式がサポートされているか確認してください（MP4, WebM）',
        'ネットワーク接続を確認してください',
      ],
      [ErrorType.VIDEO_NOT_FOUND]: [
        '設定ファイルで指定した動画パスが正しいか確認してください',
        'サーバーに動画ファイルが存在するか確認してください',
      ],
      [ErrorType.CAMERA_ACCESS_DENIED]: [
        'AR を体験するにはカメラの許可が必要です',
        'iPhone (iOS Safari): 設定 > Safari > このサイトのアドレス > カメラ > 「確認」または「許可」に変更してください',
        'Android (Chrome): アドレスバー左の 🔒 アイコン > 権限 > カメラ > 許可 に変更してください',
        '設定を変更したら、このページを再読み込みしてください',
      ],
      [ErrorType.CAMERA_NOT_AVAILABLE]: [
        'デバイスにカメラが接続されているか確認してください',
        '他のアプリがカメラを使用していないか確認してください',
      ],
      [ErrorType.CAMERA_NOT_FOUND]: [
        'このデバイスにカメラが搭載されているか確認してください',
        '外付けカメラを使用している場合、接続を確認してください',
      ],
      [ErrorType.CAMERA_IN_USE]: [
        '他のアプリ（Zoom、FaceTime、カメラアプリ等）がカメラを使用中の可能性があります',
        '他のアプリを閉じてから、このページを再読み込みしてください',
      ],
      [ErrorType.CAMERA_ERROR]: [
        'カメラの起動に失敗しました',
        'ページを再読み込みして、もう一度お試しください',
        '解決しない場合はデバイスを再起動してください',
      ],
      [ErrorType.POSTER_NOT_FOUND]: [
        'URLに指定されたポスターIDが正しいか確認してください',
        'QRコードを再度スキャンしてください',
      ],
      [ErrorType.NETWORK_ERROR]: [
        'ネットワーク接続を確認してください',
        '通信環境の良い場所で再度お試しください',
        'ページを再読み込みしてください',
      ],
      [ErrorType.TARGET_LOAD_ERROR]: [
        'マーカーデータの読み込みに失敗しました',
        'ネットワーク接続を確認してページを再読み込みしてください',
      ],
      [ErrorType.DETECTION_FAILED]: [
        'マーカー検出処理の起動に失敗しました',
        'ページを再読み込みして、もう一度お試しください',
      ],
      [ErrorType.DETECTION_TIMEOUT]: [
        'ポスターがカメラの視野に入っているか確認してください',
        'ポスターとカメラの距離を調整してください（30cm〜1m推奨）',
        '照明が十分か確認してください',
        'ポスター画像が反射やぼやけていないか確認してください',
      ],
      [ErrorType.QR_INVALID]: [
        'このQRコードには対応していません',
        '対応するポスターのQRコードを再度スキャンしてください',
      ],
      [ErrorType.INITIALIZATION_ERROR]: [
        'ブラウザがWebGLに対応しているか確認してください',
        'ページをリロードして再試行してください',
      ],
    };

    return hints[errorType] || ['技術サポートにお問い合わせください'];
  }

  /**
   * コンソールにログ出力
   * @param {Object} errorInfo - エラー情報
   * @private
   */
  _logToConsole(errorInfo) {
    const { level, errorType, userMessage, technicalMessage, context } = errorInfo;

    const emoji =
      {
        [ErrorLevel.CRITICAL]: '🔴',
        [ErrorLevel.ERROR]: '❌',
        [ErrorLevel.WARNING]: '⚠️',
        [ErrorLevel.INFO]: 'ℹ️',
      }[level] || '❗';

    console.group(`${emoji} ${level.toUpperCase()}: ${errorType}`);
    console.error('ユーザーメッセージ:', userMessage);
    console.error('技術詳細:', technicalMessage);
    if (Object.keys(context).length > 0) {
      console.error('コンテキスト:', context);
    }
    console.error('トラブルシューティング:', errorInfo.troubleshooting);
    if (errorInfo.stack) {
      console.error('スタックトレース:', errorInfo.stack);
    }
    console.groupEnd();
  }

  /**
   * エラー履歴に追加
   * @param {Object} errorInfo - エラー情報
   * @private
   */
  _addToHistory(errorInfo) {
    this.errorHistory.push(errorInfo);
    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory.shift();
    }
  }

  /**
   * エラー履歴を取得
   * @returns {Array<Object>}
   */
  getErrorHistory() {
    return [...this.errorHistory];
  }

  /**
   * 最新のエラーを取得
   * @returns {Object|null}
   */
  getLastError() {
    return this.errorHistory.length > 0 ? this.errorHistory[this.errorHistory.length - 1] : null;
  }

  /**
   * エラー統計を取得
   * @returns {Object}
   */
  getErrorStats() {
    const stats = {
      total: this.errorHistory.length,
      byType: {},
      byLevel: {},
    };

    this.errorHistory.forEach((errorInfo) => {
      // タイプ別
      stats.byType[errorInfo.errorType] = (stats.byType[errorInfo.errorType] || 0) + 1;

      // レベル別
      stats.byLevel[errorInfo.level] = (stats.byLevel[errorInfo.level] || 0) + 1;
    });

    return stats;
  }

  /**
   * エラー履歴をクリア
   */
  clearHistory() {
    this.errorHistory = [];
  }
}
